import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../layout";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import ContainedButton from "../../forms/button/ContainedButton";
import OutlinedButton from "../../forms/button/OutlinedButton";
import CustomInput from "../../forms/input/Input";
import CustomSwitch from "../../components/switch/Switch";
import DatePickerInput from "../../forms/input/DatePickerInput";
import AddSlabModal from "./AddSlabModal";
import { Layers, Clock, Pencil, Trash2 } from "lucide-react";
import { IconButton, Tooltip } from "@mui/material";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  createTariffV2,
  updateTariffV2,
  deleteTariffSlabV2,
  getTariffDetailsV2,
  TariffV2,
  TimeOfUseV2,
} from "../../utils/tariffServiceV2";
import CustomToast from "../../components/toast/CustomToast";
import { ToastState } from "../../types";
import moment from "moment";

const formatTime = (time: any) => {
  if (!time) return "-";
  if (typeof time === "string") {
    if (!time.includes(":")) return time || "-";
    // Handle string format "HH:mm:ss" or similar
    return time.split(":").slice(0, 2).join(":");
  }
  const h = String(time.hour ?? 0).padStart(2, "0") || "00";
  const m = String(time.minute ?? 0).padStart(2, "0") || "00";
  if (h === "undefined" || m === "undefined") return "-";
  return `${h}:${m}`;
};

const TariffFormV2: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editData = location.state?.tariff as TariffV2 | undefined;

  const [formData, setFormData] = useState({
    tariffCode: "",
    name: "",
    description: "",
    effectiveFrom: new Date(),
    effectiveTo: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // Default +1 day
    isActive: true,
  });

  const [slabs, setSlabs] = useState<any[]>(editData?.slabs || []);
  const [isAddSlabModalOpen, setIsAddSlabModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [slabToDelete, setSlabToDelete] = useState<any>(null);
  const [slabToEdit, setSlabToEdit] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        tariffCode: editData.tariffCode,
        name: editData.name,
        description: editData.description,
        effectiveFrom: new Date(editData.effectiveFrom),
        effectiveTo: new Date(editData.effectiveTo),
        isActive: editData.isActive,
      });
      setSlabs(editData.slabs || []);
    }
  }, [editData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleDateChange = (name: string, date: Date | null) => {
    if (date) {
      setFormData((prev) => ({ ...prev, [name]: date }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.tariffCode || !formData.name) {
      setToast({
        message: "Tariff Code and Name are required",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      let response;
      if (editData) {
        response = await updateTariffV2(editData.id, {
          name: formData.name,
          description: formData.description,
          effectiveTo: formData.effectiveTo.toISOString(),
          isActive: formData.isActive,
        });
      } else {
        response = await createTariffV2({
          tariffCode: formData.tariffCode,
          name: formData.name,
          description: formData.description,
          effectiveFrom: formData.effectiveFrom.toISOString(),
          effectiveTo: formData.effectiveTo.toISOString(),
        });
      }

      if (response && response.success) {
        setToast({
          message: editData
            ? "Tariff updated successfully"
            : "Tariff created successfully",
          severity: "success",
        });
        setTimeout(() => navigate("/new-tariffs"), 1500);
      } else {
        setToast({
          message: response?.message || "Operation failed",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving tariff:", error);
      setToast({
        message: "An error occurred while saving",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const refreshSlabs = async () => {
    if (!editData?.id) return;
    try {
      const response = await getTariffDetailsV2(editData.id);
      if (response && response.success) {
        setSlabs(response.data.slabs || []);
      }
    } catch (error) {
      console.error("Error refreshing slabs:", error);
    }
  };

  const handleEditClick = (slab: any) => {
    setSlabToEdit(slab);
    setIsAddSlabModalOpen(true);
  };

  const handleDeleteClick = (slab: any) => {
    setSlabToDelete(slab);
    setDeleteModalOpen(true);
  };

  const confirmDeleteSlab = async () => {
    if (!editData?.id || !slabToDelete?.id) return;

    try {
      setIsDeleting(true);
      const response = await deleteTariffSlabV2(editData.id, slabToDelete.id);
      if (response && response.success) {
        setToast({
          message: "Slab deleted successfully",
          severity: "success",
        });
        setDeleteModalOpen(false);
        setSlabToDelete(null);
        refreshSlabs();
      } else {
        setToast({
          message: response?.message || "Failed to delete slab",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting slab:", error);
      setToast({
        message: "An error occurred while deleting the slab",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Typography variant="h5" fontWeight={700}>
            {editData ? "Edit Tariff" : "Create New Tariff"}
          </Typography>
          <Stack direction="row" gap={2}>
            <OutlinedButton label="Cancel" onClick={() => navigate(-1)} />
            <ContainedButton
              label={editData ? "Update" : "Save"}
              onClick={handleSubmit}
              loading={loading}
              sx={{ px: 4 }}
            />
          </Stack>
        </Stack>
        <Divider />
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Tariff Code"
                name="tariffCode"
                value={formData.tariffCode}
                onChange={handleChange}
                disabled={!!editData}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Tariff Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePickerInput
                label="Effective From"
                value={formData.effectiveFrom}
                onChange={(date) => handleDateChange("effectiveFrom", date)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePickerInput
                label="Effective To"
                value={formData.effectiveTo}
                onChange={(date) => handleDateChange("effectiveTo", date)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body1" fontWeight={500}>
                  Active Status
                </Typography>
                <CustomSwitch
                  value={formData.isActive}
                  onChange={handleSwitchChange}
                />
              </Stack>
            </Grid>
          </Grid>

          {editData && (
            <>
              <Divider sx={{ my: 4 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ color: "#1064ED", display: "flex" }}>
                    <Layers size={20} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#1E293B" }}
                  >
                    Tariff Slabs
                  </Typography>
                </Stack>
                <ContainedButton
                  label="Add Slab"
                  onClick={() => setIsAddSlabModalOpen(true)}
                  sx={{ px: 3 }}
                />
              </Stack>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F8F9FB" }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                      >
                        Slab Name
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="center"
                      >
                        Slab Type
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="right"
                      >
                        Min Units
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="right"
                      >
                        Max Units
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="right"
                      >
                        Rate
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="right"
                      >
                        Fixed Charge
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="center"
                      >
                        TOU
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="center"
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#475569", py: 1.5 }}
                        align="center"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slabs && slabs.length > 0 ? (
                      slabs.map((slab) => (
                        <TableRow
                          key={slab.id}
                          sx={{ "&:hover": { backgroundColor: "#F1F5F9" } }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: "#1E293B" }}>
                            {slab.slabName || "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={slab.slabType || "-"}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: "9px",
                                height: "18px",
                                color: "#64748B",
                                borderColor: "#E2E8F0",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {slab.minUnits ?? "-"}
                          </TableCell>
                          <TableCell align="right">
                            {slab.maxUnits ?? "-"}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, color: "#1064ED" }}
                          >
                            ₹{slab.ratePerUnit ?? "-"}
                          </TableCell>
                          <TableCell align="right">
                            ₹{slab.fixedCharge ?? "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Clock size={12} color="#64748B" />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, color: "#475569" }}
                              >
                                {slab.timeOfUseStart || slab.timeOfUseEnd
                                  ? `${formatTime(slab.timeOfUseStart)} - ${formatTime(slab.timeOfUseEnd)}`
                                  : "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={slab.isActive ? "Active" : "Inactive"}
                              size="small"
                              variant="outlined"
                              color={slab.isActive ? "success" : "default"}
                              sx={{
                                fontWeight: 700,
                                fontSize: "9px",
                                height: "20px",
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                            >
                              <Tooltip title="Edit Slab">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: "#64748B",
                                    "&:hover": { color: "#1064ED" },
                                  }}
                                  onClick={() => handleEditClick(slab)}
                                >
                                  <Pencil size={16} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Slab">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: "#64748B",
                                    "&:hover": { color: "#EF4444" },
                                  }}
                                  onClick={() => handleDeleteClick(slab)}
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              py: 3,
                              color: "#64748B",
                              fontStyle: "italic",
                            }}
                          >
                            No slabs available. Add a new slab to this tariff.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <AddSlabModal
                open={isAddSlabModalOpen}
                onClose={() => {
                  setIsAddSlabModalOpen(false);
                  setSlabToEdit(null);
                }}
                tariffId={editData.id}
                editData={slabToEdit}
                onSuccess={() => {
                  setToast({
                    message: slabToEdit
                      ? "Slab updated successfully"
                      : "Slab added successfully",
                    severity: "success",
                  });
                  setIsAddSlabModalOpen(false);
                  setSlabToEdit(null);
                  refreshSlabs();
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        confirmModal={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onClick={confirmDeleteSlab}
        title="Delete Slab"
        body={`Are you sure you want to delete the slab "${slabToDelete?.slabName}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      <CustomToast
        open={toast.message}
        severity={toast.severity as any}
        setToast={setToast}
      />
    </Layout>
  );
};

export default TariffFormV2;
